/**
 * useTutorLive — la salle du tuteur, branchée : la salle commune (`useLiveRoom`, `role: "tutor"`) et ce
 * que l'ancien live faisait autour d'elle pour le tuteur, sans écran.
 *
 * Porté de `common/liveMode/LiveMainLayout.js`, `liveV3/LiveContainerV3.js` et `liveV3/TutorLiveScreen.js` :
 *  - **l'appel** : `joinCallV2` au montage, avec le tampon d'événements de la séance
 *    (`LiveEventBuffer.init`) ; au démontage `leaveCallV2`, le tampon vidé, la salle coupée
 *    (`TutorLiveScreen.js:171-189`). Tenus pour la séance (`callHold`, comme l'élève) : un écran remonté
 *    aussitôt reprend l'appel sans écrire `left_call` puis `joined_call` ;
 *  - **les métriques** (`getMetricsV2`) : `no_cache=1` à l'entrée, puis à chaque changement des présents,
 *    `no_cache=1` quand un id manque au relevé précédent (`metricsRead`) ;
 *  - **les minuteurs de 5 s** (`batchUpdateTimers`, `timerUpdates`), `updateLastConnected` toutes les
 *    5 min, l'écran gardé allumé au natif, `cam_toggle` écrit une fois connecté (`useInitialCamRecord`) ;
 *  - **la caméra et le micro** à l'entrée (`requestMediaAccess`) : demandés au web (l'ancien
 *    `PermissionsManager`), et au natif sans capture, la salle ne se connectant qu'après la réponse ; un fil
 *    d'Ariane avant et après (durée, réponse) ;
 *  - **les traces** d'un gel ou d'un « Connexion… » sans fin (`useTutorLiveTraces`) : les journaux de
 *    LiveKit, le premier plan, CallKeep qui ne démarre pas ;
 *  - **le débordement** sur le live suivant (`live_signals.tutor_overrun` du relevé) et son acquit ;
 *  - **la sortie forcée** : le relevé qui dit de sortir (403, 402, 404) est retenu, le tuteur le lit,
 *    puis sort (`disconnectFromLive`) ;
 *  - **« Annuler »** sur l'écran de connexion : `leaveLiveSession`, puis la sortie ;
 *  - **une notification touchée** (natif) : son issue (`tutorLiveTapOutcome`) ; la réponse du support
 *    pose `pendingIntercomOpen` ici, le reste passe à l'écran ;
 *  - **les résumés du champion que l'écouteur global a sonnés** (`summariesAnnounced`) : l'écran ne rejoue pas
 *    leur jingle.
 *
 * Ce qu'il ne fait pas, voulu : `MuteCurrentUser` (chacun de ses effets s'arrête sur le rôle élève :
 * rien pour le tuteur), les sons de connexion et de départ d'un élève, les attributs, `getCallV2` à la
 * reprise, la qualité de connexion (tous joués par `LKEvents`, monté pour toute l'app), la session audio
 * (l'ancien tuteur n'en démarrait pas). `NotificationSubscriptionHandler` n'est plus monté par la salle :
 * l'enregistrement de l'appareil aux notifications ne se fait plus en plein live (la boîte système ne
 * s'ouvre plus par-dessus la salle).
 */
import { useConnectionState } from "@livekit/components-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector, useStore } from "react-redux"

import { ackOverrun as ackOverrunAction, batchUpdateTimers } from "../../../data/redux/actions/liveMode"
import { updateLastConnected } from "../../../data/redux/actions/authTokens"
import { setPendingIntercomOpen } from "../../../data/redux/actions/intercom"
import { isOverrunAcknowledged } from "../../../data/redux/reducers/data/tutorOverrunAck"
import LiveEventBuffer from "../../../data/services/LiveEventBuffer"
import { liveService } from "../../../data/services/liveService"
import { liveServiceV2 } from "../../../data/services/liveServiceV2"
import { hasRole } from "../../../data/utility/ability"
import { apiCall, type ApiService } from "../../shared/apiCall"
import { createTelemetry, type EngineTelemetry } from "../../shared/telemetry"
import { useInterval } from "../../shared/useInterval"
import { IS_WEB } from "../../shared/platform"
import { createCallHold } from "../room/callHold"
import { type RoomConnection, type RoomExit } from "../room/roomMachine"
import { useDisconnectFromLive } from "../room/useDisconnectFromLive"
import { useInitialCamRecord } from "../room/useInitialCamRecord"
import { useLiveRoom, type LiveRoom } from "../room/useLiveRoom"
import { useTutorKeepAwake } from "./tutorKeepAwake"
import { watchPermissionPrompt } from "../../precall/permissionPrompt"
import { MEDIA_ACCESS_HOW, requestMediaAccess } from "./tutorMediaAccess"
import { type TutorTapOutcome } from "./tutorLiveNotificationTap"
import {
    announcedSummaryId,
    enrolledStudentIds,
    metricsParams,
    metricsRead,
    noStudentYetFrom,
    overrunToShow,
    timerUpdates,
    type ApiTutorOverrun,
    type NoticeSession,
} from "./tutorRoomRules"
import { useTutorLiveNotificationTap } from "./useTutorLiveNotificationTap"
import { useTutorLiveTraces } from "./useTutorLiveTraces"

/** Le minuteur des temps d'échange, du tuteur seulement (`LiveMainLayout.js:113`). */
export const TUTOR_TIMERS_EVERY_MS = 5 * 1000
/** `updateLastConnected`, comme `LiveContainerV3.js:60-64`. */
export const TUTOR_LAST_CONNECTED_EVERY_MS = 5 * 60 * 1000

/**
 * Le protocole de la salle, geste par geste : ce que l'ancien appelait, et que ce crochet appelle à
 * l'identique (décision 7 : mêmes appels, mêmes paramètres). La salle n'envoie aucun message de salle
 * ni attribut elle-même : ceux du tuteur restent à `LKEvents`, global. Tenu par
 * `useTutorLive.hook.test.tsx`, une ligne par test.
 */
export const TUTOR_ROOM_EQUIVALENCE = [
    {
        gesture: "entrer dans la salle",
        legacy: "TutorLiveScreen.js:171-175",
        service: "liveServiceV2.joinCallV2",
        routeParams: "{ id }",
        data: null,
        message: null,
    },
    {
        gesture: "quitter la salle (l'écran démonté)",
        legacy: "TutorLiveScreen.js:177-188",
        service: "liveServiceV2.leaveCallV2",
        routeParams: "{ id }",
        data: null,
        message: null,
    },
    {
        gesture: "le relevé (montage, puis 60 s)",
        legacy: "LiveMainLayout.js:116-140",
        service: "liveServiceV2.getLiveSessionV2",
        routeParams: "{ id }",
        data: null,
        message: null,
    },
    {
        gesture: "les métriques à l'entrée",
        legacy: "LiveMainLayout.js:135-140",
        service: "liveServiceV2.getMetricsV2",
        routeParams: "{ id }",
        data: '{ no_cache: "1" }',
        message: null,
    },
    {
        gesture: "les métriques, un présent arrive",
        legacy: "LiveMainLayout.js:142-163",
        service: "liveServiceV2.getMetricsV2",
        routeParams: "{ id }",
        data: '{ no_cache: "1" }, sinon {}',
        message: null,
    },
    {
        gesture: "« Annuler » sur l'écran de connexion",
        legacy: "TutorLiveScreen.js:375-379, ConnectingView.js:58-66",
        service: "liveService.leaveLiveSession",
        routeParams: "{ id }",
        data: null,
        message: null,
    },
] as const

export type ApiTutorLiveSession = NoticeSession & {
    id?: number
    conference_link?: string | null
    thread_id?: string | number | null
    thread_ids?: Record<string, string | number | null | undefined> | null
    live_room?: { type?: string | null; version?: string | null; version_ft?: unknown } | null
} & Record<string, unknown>

export type ApiTutorLiveUser = {
    id?: number
    email?: string | null
    roles?: string[] | null
    display_name?: string | null
    firstname?: string | null
    avatar?: string | null
    live_sessions?: ApiTutorLiveSession[] | null
} & Record<string, unknown>

/** Une sortie dite par le relevé, retenue jusqu'à ce que le tuteur l'ait lue. */
export type TutorLiveExit = { exit: RoomExit; message: string | null }

export type TutorLive = {
    liveSessionId: number | null
    user: ApiTutorLiveUser | null
    liveSession: ApiTutorLiveSession | null
    room: LiveRoom
    connection: RoomConnection
    /** La salle de cette séance est connectée. */
    connected: boolean
    /**
     * La caméra et le micro ont reçu leur réponse. `granted` : la réponse du navigateur au web, celle des
     * questions du système au natif. Au web, la demande est suivie par `watchPermissionPrompt` : la salle lit la
     * question restée à l'écran par `usePermissionPrompt` (la règle de l'élève).
     */
    media: { checked: boolean; granted: boolean | null }
    /** Les élèves inscrits, et ceux qui sont dans la salle (ids). */
    enrolledIds: string[]
    presentIds: string[]
    /** Trois minutes après le début de la séance (`noStudentYetFrom`). */
    noStudentYetDue: boolean
    overrun: (ApiTutorOverrun & { next_occurrence_id: number | string }) | null
    exit: TutorLiveExit | null
    /** La dernière notification touchée que l'écran doit jouer, et son numéro (une fois chacune). */
    tap: { outcome: TutorTapOutcome; seq: number } | null
    /**
     * Les conversations dont l'écouteur global a joué le jingle du résumé (`agentConversationCompleted` à
     * résumé, `announcedSummaryId`), entendues depuis l'entrée dans la salle. Lu au moment de sonner, pas suivi :
     * l'ensemble grandit sans redessiner l'écran.
     */
    summariesAnnounced: ReadonlySet<string>
    telemetry: EngineTelemetry
    actions: {
        ackOverrun: () => void
        /** La sortie dite par le relevé, lue : le tuteur sort. */
        confirmExit: () => void
        /** « Annuler » de l'écran de connexion. */
        cancelConnection: () => void
        /** « Réessayer » : la salle relancée (web, ou passé le plafond). */
        retryConnection?: () => void
        dismissTap: () => void
    }
}

type RootState = {
    data?: {
        currentUser?: { object?: ApiTutorLiveUser | null }
        tutorOverrunAck?: unknown
    }
    liveMode?: {
        liveSessionId?: number | null
        liveParticipants?: number[] | null
        timesSinceLastCall?: Record<string, number> | unknown[] | null
        timesSinceLastCallCalculatedAt?: string | null
        activeTime?: { id: number; time: number }[] | null
        activeTimeCalculatedAt?: string | null
        soundModeIds?: number[] | null
        isSoundModeProcessing?: boolean
        liveRoomSignals?:
            ({ liveSessionId?: number | null; tutor_overrun?: ApiTutorOverrun | null } & Record<string, unknown>) | null
    }
}

/** L'appel ouvert d'une séance : sa réponse, tenue par ses écrans. */
type TutorCall = { joined: Promise<unknown> }
const tutorCalls = createCallHold<TutorCall>()

const NO_PARTICIPANTS: number[] = []

export function useTutorLive(options: { telemetry?: EngineTelemetry } = {}): TutorLive {
    const dispatch = useDispatch()
    const telemetry = useMemo(
        () => options.telemetry ?? createTelemetry("live-tutor", { role: "tutor" }),
        [options.telemetry]
    )

    const liveSessionId = useSelector((state: RootState) => state.liveMode?.liveSessionId ?? null)
    const user = useSelector((state: RootState) => state.data?.currentUser?.object ?? null)
    const liveSession = useMemo(
        () => user?.live_sessions?.find((session) => session?.id === liveSessionId) ?? null,
        [user?.live_sessions, liveSessionId]
    )
    const isTutor = hasRole(user, "tutor") === true
    const participants = useSelector((state: RootState) => state.liveMode?.liveParticipants ?? NO_PARTICIPANTS)
    const userRef = useRef(user)
    userRef.current = user

    /* ── la caméra et le micro ── */
    // Avant la salle : au natif, elle attend la réponse pour se connecter (`holdConnection`).
    const [media, setMedia] = useState<TutorLive["media"]>({ checked: false, granted: null })
    useEffect(() => {
        let mounted = true
        const startedAt = Date.now()
        telemetry.transition("media_unchecked", "media_requesting", "media_access", { how: MEDIA_ACCESS_HOW })
        // Au web, la question du navigateur peut attendre sans fin (sa bulle, que rien n'oblige à voir) : la
        // demande est suivie comme celles de l'élève, et la salle dit la question restée à l'écran
        // (`usePermissionPrompt`, qui ne la dit pas quand l'accès est déjà acquis). Au natif, ne fait rien.
        watchPermissionPrompt("both", requestMediaAccess())
            .then((granted) => {
                telemetry.transition("media_requesting", "media_checked", "media_access", {
                    how: MEDIA_ACCESS_HOW,
                    granted,
                    durationMs: Date.now() - startedAt,
                    unmounted: !mounted,
                })
                if (mounted) setMedia({ checked: true, granted })
            })
            .catch((cause: unknown) => {
                // Une lecture impossible ne dit rien de la caméra : la salle s'ouvre, « on ne sait pas ».
                telemetry.error("media_access", cause, { how: MEDIA_ACCESS_HOW, durationMs: Date.now() - startedAt })
                if (mounted) setMedia({ checked: true, granted: null })
            })
        return () => {
            mounted = false
        }
    }, [telemetry])

    /* ── la salle ── */
    const [exit, setExit] = useState<TutorLiveExit | null>(null)
    // Le canal est lu par l'écouteur global (`LKEvents`) ; la salle n'y retient que les résumés qu'il a sonnés.
    const summariesAnnouncedRef = useRef<Set<string>>(new Set())
    const room = useLiveRoom({
        liveSessionId,
        role: "tutor",
        telemetry,
        onData: (event) => {
            const announced = announcedSummaryId(event)
            if (announced != null) summariesAnnouncedRef.current.add(announced)
        },
        onExit: (kind, message) => setExit({ exit: kind, message }),
        holdConnection: !IS_WEB && !media.checked,
    })
    const roomRef = useRef(room)
    roomRef.current = room
    const connection = room.state.connection
    const connected = connection === "connected"

    /* ── l'appel ── */
    useEffect(() => {
        if (liveSessionId == null) return undefined
        const held = tutorCalls.take(liveSessionId, () => {
            const joined = apiCall(dispatch as never, liveServiceV2.joinCallV2 as ApiService, {
                routeParams: { id: liveSessionId },
            }).catch((cause: unknown) => telemetry.error("join_call", cause))
            LiveEventBuffer.getInstance().init(liveSessionId, userRef.current?.id)
            return { joined }
        })
        if (held.resumed) telemetry.transition("released", "held", "call_resumed")
        return () => {
            held.release(() => {
                apiCall(dispatch as never, liveServiceV2.leaveCallV2 as ApiService, {
                    routeParams: { id: liveSessionId },
                }).catch((cause: unknown) => telemetry.error("leave_call", cause))
                void LiveEventBuffer.getInstance().destroy()
                roomRef.current.room?.disconnect(true).catch((cause: unknown) => {
                    telemetry.error("disconnect", cause)
                })
            })
        }
        // Une fois par séance, comme l'ancien.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveSessionId])

    // `cam_toggle` à la première connexion du SDK (l'ancien lisait `useConnectionState`, pas la séance).
    const sdkConnected = useConnectionState() === "connected"
    useInitialCamRecord(sdkConnected, room.room)
    useTutorKeepAwake()
    useTutorLiveTraces(telemetry, liveSessionId)

    /* ── les métriques ── */
    const readMetrics = useCallback(
        (noCache: boolean) => {
            if (!isTutor || liveSessionId == null) return
            apiCall(dispatch as never, liveServiceV2.getMetricsV2 as ApiService, {
                routeParams: { id: liveSessionId },
                data: metricsParams(noCache),
            }).catch((cause: unknown) => telemetry.error("metrics", cause))
        },
        [dispatch, isTutor, liveSessionId, telemetry]
    )
    const readMetricsRef = useRef(readMetrics)
    readMetricsRef.current = readMetrics
    useEffect(() => {
        // `no_cache` à l'entrée (`LiveMainLayout.js:135-140`).
        readMetricsRef.current(true)
    }, [liveSessionId])
    const previousParticipantsRef = useRef<ReadonlySet<number>>(new Set())
    useEffect(() => {
        if (!isTutor) return
        const decision = metricsRead(previousParticipantsRef.current, participants)
        if (!decision.read) return
        readMetricsRef.current(decision.noCache)
        previousParticipantsRef.current = decision.next
        // L'ancien relisait sur la liste seule (`[props.participants]`).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [participants])

    /* ── les minuteurs ── */
    // Lu à chaque tic, pas suivi : l'écran ne se redessine pas à chaque minuteur.
    const store = useStore()
    useInterval(
        () => {
            const timerState = (store.getState() as RootState).liveMode
            const updates = timerUpdates({
                timesSinceLastCall: timerState?.timesSinceLastCall,
                activeTime: timerState?.activeTime,
                soundModeIds: timerState?.soundModeIds,
                participants: timerState?.liveParticipants,
                soundModeProcessing: timerState?.isSoundModeProcessing === true,
            })
            if (updates == null) return
            dispatch(
                batchUpdateTimers(
                    updates.timesSinceLastCallUpdates,
                    updates.activeTimeUpdates,
                    timerState?.timesSinceLastCallCalculatedAt || timerState?.activeTimeCalculatedAt
                ) as never
            )
        },
        isTutor ? TUTOR_TIMERS_EVERY_MS : null
    )
    useInterval(() => {
        const current = userRef.current
        if (current?.email) dispatch(updateLastConnected(current) as never)
    }, TUTOR_LAST_CONNECTED_EVERY_MS)

    /* ── les élèves ── */
    const enrolledIds = useMemo(() => enrolledStudentIds(liveSession), [liveSession])
    const roomIds = room.participants
        .filter((participant) => !participant.isLocal)
        .map((participant) => participant.id)
        .join("|")
    const presentIds = useMemo(
        () => [...new Set(roomIds === "" ? [] : roomIds.split("|"))].filter((id) => enrolledIds.includes(id)),
        [roomIds, enrolledIds]
    )
    const dueAt = noStudentYetFrom(liveSession)
    const [noStudentYetDue, setNoStudentYetDue] = useState(false)
    useEffect(() => {
        if (dueAt == null) {
            setNoStudentYetDue(false)
            return undefined
        }
        const wait = dueAt - Date.now()
        if (wait < 0) {
            setNoStudentYetDue(true)
            return undefined
        }
        setNoStudentYetDue(false)
        const timer = setTimeout(() => setNoStudentYetDue(true), wait + 1)
        return () => clearTimeout(timer)
    }, [dueAt])

    /* ── le débordement ── */
    const signals = useSelector((state: RootState) => state.liveMode?.liveRoomSignals ?? null)
    const ackState = useSelector((state: RootState) => state.data?.tutorOverrunAck)
    const overrun = useMemo(() => {
        const ours = liveSessionId != null && signals?.liveSessionId === liveSessionId ? signals : null
        return overrunToShow(
            ours?.tutor_overrun,
            (occurrenceId) => isOverrunAcknowledged(ackState, occurrenceId) === true
        )
    }, [signals, liveSessionId, ackState])
    const overrunId = overrun?.next_occurrence_id ?? null
    const ackOverrun = useCallback(() => {
        if (overrunId != null) dispatch(ackOverrunAction(overrunId) as never)
    }, [dispatch, overrunId])

    /* ── la sortie ── */
    const disconnect = useDisconnectFromLive() as { disconnectFromLive: () => Promise<void> }
    const leave = useCallback(() => {
        roomRef.current.closeOutage()
        disconnect.disconnectFromLive().catch((cause: unknown) => telemetry.error("disconnect_from_live", cause))
    }, [disconnect, telemetry])
    const confirmExit = useCallback(() => {
        setExit(null)
        leave()
    }, [leave])
    const cancelConnection = useCallback(() => {
        if (liveSessionId == null) return
        // L'ancien sortait au succès de `leaveLiveSession` (`LiveModeFooter.js:360-366`) : sans réseau, le
        // tuteur restait sur « Connexion… ». Ici, la sortie suit la réponse, quelle qu'elle soit.
        apiCall(dispatch as never, liveService.leaveLiveSession as ApiService, { routeParams: { id: liveSessionId } })
            .catch((cause: unknown) => telemetry.error("leave_session", cause))
            .finally(leave)
    }, [dispatch, liveSessionId, leave, telemetry])

    /* ── les notifications touchées ── */
    const [tap, setTap] = useState<TutorLive["tap"]>(null)
    const tapSeqRef = useRef(0)
    useTutorLiveNotificationTap(liveSession, (outcome) => {
        if (outcome.kind === "none") return
        if (outcome.kind === "support") {
            dispatch(setPendingIntercomOpen(true, outcome.conversationId as never) as never)
            return
        }
        tapSeqRef.current += 1
        setTap({ outcome, seq: tapSeqRef.current })
    })
    const dismissTap = useCallback(() => setTap(null), [])

    return {
        liveSessionId,
        user,
        liveSession,
        room,
        connection,
        connected,
        media,
        enrolledIds,
        presentIds,
        noStudentYetDue,
        overrun,
        exit,
        tap,
        summariesAnnounced: summariesAnnouncedRef.current,
        telemetry,
        actions: {
            ackOverrun,
            confirmExit,
            cancelConnection,
            retryConnection: room.webReconnect.onRetry,
            dismissTap,
        },
    }
}
